import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBookOpen,
  FiTarget,
  FiClock,
  FiCheck,
  FiX,
  FiArrowRight,
  FiAlertCircle,
  FiZap,
  FiAward,
  FiTrendingUp,
  FiStar,
  FiActivity,
  FiCalendar,
  FiBarChart,
  FiUser,
  FiPercent,
  FiRefreshCw,
} from 'react-icons/fi';
import InfoTooltip from '../../../components/InfoTooltip';
import {
  getCachedComprehensiveProgress,
  getVocabularyProgressFromComprehensive,
  getTextCompletionProgressFromComprehensive,
  getSentenceEquivalenceProgressFromComprehensive,
  getReadingComprehensionProgressFromComprehensive,
  getOverallSummaryFromComprehensive,
  getPerformanceAnalyticsFromComprehensive,
  getComprehensiveVerbalProgress,
  getReadinessAnalyticsFromComprehensive,
  getStruggleSpotsFromComprehensive,
  mapBackendVerbalRecommendations,
} from './api/progressAPI';
import {
  displayToast,
  redirectToLoginWithReturn,
} from '../../../utils/utils';
import { getErrorMessage } from '../../../utils/getErrorMessage';


const buildComponentScoresFromBackend = (
  readinessAnalytics,
  vocabularyData,
  textCompletionData,
  sentenceEquivalenceData,
  readingComprehensionData
) => {
  const backendComponents = readinessAnalytics?.components || {};
  const scoreFor = (key, fallback = 0) =>
    backendComponents[key]?.readiness_score ?? fallback;

  return {
    vocabulary: {
      score: scoreFor('vocabulary', vocabularyData.readinessScore || 0),
      ready: scoreFor('vocabulary') >= 75,
      estimated: false,
      wordsMastered: vocabularyData.masteredWords,
      wordsEncountered: vocabularyData.totalWords,
      accuracy: vocabularyData.accuracy,
      masteryRate: vocabularyData.masteryRate,
    },
    textCompletion: {
      score: scoreFor('text_completion', textCompletionData.readinessScore || 0),
      ready: scoreFor('text_completion') >= 75,
      estimated: false,
      accuracy: textCompletionData.accuracy,
      questionsAttempted: textCompletionData.totalQuestions,
      correctAnswers: textCompletionData.correctAnswers,
      readinessScore: textCompletionData.readinessScore,
    },
    sentenceEquivalence: {
      score: scoreFor(
        'sentence_equivalence',
        sentenceEquivalenceData.readinessScore || 0
      ),
      ready: scoreFor('sentence_equivalence') >= 75,
      estimated: false,
      accuracy: sentenceEquivalenceData.accuracy,
      questionsAttempted: sentenceEquivalenceData.totalQuestions,
      correctAnswers: sentenceEquivalenceData.correctAnswers,
      wordPairsMastered: sentenceEquivalenceData.wordPairsMastered,
      readinessScore: sentenceEquivalenceData.readinessScore,
    },
    readingComprehension: {
      score: scoreFor(
        'reading_comprehension',
        readingComprehensionData.readinessScore || 0
      ),
      ready: scoreFor('reading_comprehension') >= 75,
      estimated: false,
      accuracy: readingComprehensionData.accuracy,
      passagesCompleted: readingComprehensionData.totalPassages,
      questionsAttempted: readingComprehensionData.totalQuestions,
      correctAnswers: readingComprehensionData.correctAnswers,
      averageReadingSpeed: readingComprehensionData.averageReadingSpeed,
      readinessScore: readingComprehensionData.readinessScore,
    },
  };
};

// Calculate readiness score based on comprehensive backend progress data
const calculateReadinessScore = (comprehensiveData) => {
  const vocabularyData =
    getVocabularyProgressFromComprehensive(comprehensiveData);
  const textCompletionData =
    getTextCompletionProgressFromComprehensive(comprehensiveData);
  const sentenceEquivalenceData =
    getSentenceEquivalenceProgressFromComprehensive(comprehensiveData);
  const readingComprehensionData =
    getReadingComprehensionProgressFromComprehensive(comprehensiveData);
  const overallSummary = getOverallSummaryFromComprehensive(comprehensiveData);
  const readinessAnalytics =
    getReadinessAnalyticsFromComprehensive(comprehensiveData);
  const performanceAnalytics =
    getPerformanceAnalyticsFromComprehensive(comprehensiveData);

  const overallReadinessScore = Math.round(
    readinessAnalytics?.overallScore ??
      overallSummary.overallReadinessScore ??
      0
  );

  const componentScores = buildComponentScoresFromBackend(
    readinessAnalytics,
    vocabularyData,
    textCompletionData,
    sentenceEquivalenceData,
    readingComprehensionData
  );

  const backendRecommendations =
    mapBackendVerbalRecommendations(comprehensiveData);
  const recommendations =
    backendRecommendations?.length > 0
      ? backendRecommendations
      : generateRecommendations(componentScores, overallReadinessScore);

  const estimatedVerbalScore =
    readinessAnalytics?.scorePrediction?.predicted_score ??
    overallSummary.estimatedVerbalScore;

  return {
    overallScore: overallReadinessScore,
    componentScores,
    readinessLevel: getReadinessLevel(overallReadinessScore),
    backendLevel: readinessAnalytics?.level,
    confidence: readinessAnalytics?.confidence,
    breakdown: readinessAnalytics?.breakdown || {},
    areasToImprove: readinessAnalytics?.areasToImprove || [],
    performanceAnalytics,
    recommendations,
    overallSummary,
    estimatedVerbalScore,
  };
};

// Get readiness level based on score
const getReadinessLevel = (score) => {
  if (score >= 90)
    return {
      level: 'Elite Ready',
      color: 'var(--liwaza-success)',
      icon: <FiStar />,
    };
  if (score >= 80)
    return {
      level: 'Almost Ready',
      color: 'var(--liwaza-warning)',
      icon: <FiTarget />,
    };
  if (score >= 60)
    return {
      level: 'Good Progress',
      color: 'var(--liwaza-primary)',
      icon: <FiTrendingUp />,
    };
  if (score >= 40)
    return {
      level: 'Building Foundation',
      color: 'var(--liwaza-secondary)',
      icon: <FiBookOpen />,
    };
  return {
    level: 'Just Started',
    color: 'var(--liwaza-on-surface-variant)',
    icon: <FiZap />,
  };
};

// Generate personalized recommendations
const generateRecommendations = (componentScores, overallScore) => {
  const recommendations = [];

  // Check each component and provide specific recommendations
  if (!componentScores.vocabulary || componentScores.vocabulary.score < 80) {
    recommendations.push({
      priority: 'high',
      component: 'Vocabulary Mastery',
      message:
        'Focus on building your vocabulary foundation through daily article reading.',
      action: 'Continue Vocabulary Mastery',
      route: '/exams/gre/',
      activeComponent: 'vocabularymastery',
    });
  }

  if (
    !componentScores.textCompletion ||
    componentScores.textCompletion.score < 80
  ) {
    recommendations.push({
      priority: 'high',
      component: 'Text Completion',
      message:
        'Practice more Text Completion questions to improve context analysis skills.',
      action: 'Practice Text Completion',
      route: '/exams/gre/',
      activeComponent: 'textcompletion',
    });
  }

  if (
    !componentScores.sentenceEquivalence ||
    componentScores.sentenceEquivalence.score < 80
  ) {
    recommendations.push({
      priority: 'high',
      component: 'Sentence Equivalence',
      message:
        'Work on identifying word pairs that create equivalent meanings.',
      action: 'Practice Sentence Equivalence',
      route: '/exams/gre/',
      activeComponent: 'sentence',
    });
  }

  if (
    !componentScores.readingComprehension ||
    componentScores.readingComprehension.score < 80
  ) {
    recommendations.push({
      priority: 'high',
      component: 'Reading Comprehension',
      message:
        'Practice with more reading passages to improve comprehension speed and accuracy.',
      action: 'Practice Reading Comprehension',
      route: '/exams/gre/',
      activeComponent: 'reading',
    });
  }

  return recommendations;
};

export default function VerbalProgress() {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [progressData, setProgressData] = useState(null);
  const [readinessData, setReadinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [user, setUser] = useState(() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error parsing user from sessionStorage:', error);
      return null;
    }
  });
  const [userEmail, setUserEmail] = useState(null);

  // Resolve the authenticated learner from session-backed storage only.
  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
    } else {
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setUserEmail(storedEmail);
      } else {
        try {
          const sessionUser = sessionStorage.getItem('user');
          if (sessionUser) {
            const parsedUser = JSON.parse(sessionUser);
            if (parsedUser?.email) {
              setUserEmail(parsedUser.email);
              setUser(parsedUser);
            }
          }
        } catch (error) {
          console.error('Error parsing user from sessionStorage:', error);
        }
      }
    }
  }, [user]);

  // Refs to track ongoing API requests
  const ongoingRequests = useRef({});

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
      setToast,
    });
  };

  // Enhanced API functions with duplicate prevention
  const getComprehensiveProgressWithRef = async (
    userEmail,
    forceRefresh = false
  ) => {
    const requestKey = forceRefresh
      ? 'comprehensive_progress_refresh'
      : 'comprehensive_progress';

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Create new request promise using the new API
    const requestPromise = getComprehensiveVerbalProgress(
      userEmail,
      forceRefresh
    );

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  const loadProgressData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const comprehensiveProgress = await getComprehensiveProgressWithRef(
        userEmail,
        forceRefresh
      );

      setProgressData(comprehensiveProgress);
      setReadinessData(calculateReadinessScore(comprehensiveProgress));
    } catch (error) {
      console.error('VerbalProgress: Error loading progress data:', error);
      if (error?.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail) {
      loadProgressData(false);
    } else {
      setLoading(false);
    }
  }, [userEmail]);

  if (loading) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <div style={{ color: 'var(--liwaza-on-surface-variant)' }}>
          <FiActivity
            size={48}
            style={{
              marginBottom: '1rem',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div>Loading your progress...</div>
          {!userEmail && (
            <div
              style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.7 }}
            >
              Waiting for user authentication...
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show a different message if userEmail is not available
  if (!userEmail && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-on-surface-variant)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <FiUser size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
        <h3>Authentication Required</h3>
        <p>Please log in to view your GRE Verbal progress.</p>
        <button
          onClick={() => (window.location.href = '/login')}
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-error)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <h2>Error Loading Progress</h2>
        <p>{getErrorMessage(error, 'Failed to load progress data.')}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const renderReadinessScore = () => {
    if (!readinessData) return null;

    const {
      overallScore,
      readinessLevel,
      estimatedVerbalScore,
      backendLevel,
      confidence,
    } = readinessData;

    return (
      <div
        style={{
          background:
            'linear-gradient(135deg, var(--liwaza-primary-container) 0%, var(--liwaza-secondary-container) 100%)',
          padding: '2rem',
          borderRadius: '16px',
          marginBottom: '2rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <FiAward size={32} style={{ color: readinessLevel.color }} />
            <h2
              style={{
                fontSize: '2rem',
                margin: 0,
                color: 'var(--liwaza-on-primary-container)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              GRE Verbal Readiness Score
              <InfoTooltip
                content="Your readiness score is computed on the server from accuracy, practice coverage, recent trend, pacing, and consistency across vocabulary, text completion, sentence equivalence, and reading comprehension. Confidence reflects how much verbal practice data we have."
                position="bottom"
                maxWidth="350px"
              />
            </h2>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '4rem',
                  fontWeight: 'bold',
                  color: readinessLevel.color,
                  marginBottom: '0.5rem',
                }}
              >
                {overallScore}%
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                {readinessLevel.icon}
                <span
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    color: readinessLevel.color,
                  }}
                >
                  {backendLevel || readinessLevel.level}
                </span>
              </div>
              {confidence && confidence !== 'none' && (
                <div
                  style={{
                    fontSize: '0.85rem',
                    color: 'var(--liwaza-on-primary-container)',
                    opacity: 0.85,
                    marginTop: '0.25rem',
                  }}
                >
                  {confidence} confidence estimate
                </div>
              )}
            </div>

            {estimatedVerbalScore && estimatedVerbalScore !== 'TBD' && (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: 'var(--liwaza-on-primary-container)',
                    marginBottom: '0.5rem',
                  }}
                >
                  {estimatedVerbalScore}
                </div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--liwaza-on-primary-container)',
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    justifyContent: 'center',
                  }}
                >
                  Estimated Verbal Score
                  <InfoTooltip
                    content="This is your projected GRE Verbal score (130-170 scale) based on your current performance across all verbal components. Improve your readiness score to increase this estimate. A score of 160+ is considered elite level."
                    position="top"
                    maxWidth="280px"
                  />
                </div>
              </div>
            )}
          </div>

          <p
            style={{
              color: 'var(--liwaza-on-primary-container)',
              fontSize: '1rem',
              margin: 0,
              opacity: 0.9,
            }}
          >
            {overallScore >= 90
              ? "Excellent! You're ready for an elite GRE Verbal score (160+)!"
              : overallScore >= 80
                ? 'Great progress! Focus on weak areas to achieve an elite score.'
                : overallScore >= 60
                  ? 'Good foundation. Continue practicing to reach elite readiness.'
                  : 'Keep building your skills across all verbal areas.'}
          </p>
        </div>
      </div>
    );
  };

  const renderComponentProgress = () => {
    if (!progressData) return null;

    // Extract component-specific data using our helper functions
    const vocabularyData = getVocabularyProgressFromComprehensive(
      progressData,
      setToast
    );
    const textCompletionData = getTextCompletionProgressFromComprehensive(
      progressData,
      setToast
    );
    const sentenceEquivalenceData =
      getSentenceEquivalenceProgressFromComprehensive(progressData, setToast);
    const readingComprehensionData =
      getReadingComprehensionProgressFromComprehensive(progressData, setToast);

    const components = [
      {
        key: 'vocabulary',
        title: 'Vocabulary Mastery',
        icon: <FiBookOpen />,
        stats: [
          { label: 'Words Mastered', value: vocabularyData.masteredWords || 0 },
          { label: 'Words Encountered', value: vocabularyData.totalWords || 0 },
          {
            label: 'Accuracy',
            value: `${Math.round(vocabularyData.accuracy || 0)}%`,
          },
          {
            label: 'Current Streak',
            value: `${vocabularyData.streak || 0} days`,
          },
        ],
        route: '/exams/gre/',
        activeComponent: 'vocabularymastery',
      },
      {
        key: 'textCompletion',
        title: 'Text Completion',
        icon: <FiTarget />,
        stats:
          textCompletionData.totalQuestions > 0
            ? [
                {
                  label: 'Questions Attempted',
                  value: textCompletionData.totalQuestions,
                },
                {
                  label: 'Accuracy',
                  value: `${Math.round(textCompletionData.accuracy)}%`,
                },
                {
                  label: 'Correct Answers',
                  value: textCompletionData.correctAnswers,
                },
                {
                  label: 'Readiness Score',
                  value: `${Math.round(textCompletionData.readinessScore)}%`,
                },
              ]
            : [
                { label: 'Practice Available', value: 'Ready' },
                {
                  label: 'Estimated Accuracy',
                  value: `${Math.round(Math.min(textCompletionData.accuracy + 10, 90))}%`,
                },
                { label: 'Status', value: 'Not Started' },
                { label: 'Target Score', value: '85%+' },
              ],
        route: '/exams/gre/',
        activeComponent: 'textcompletion',
      },
      {
        key: 'sentenceEquivalence',
        title: 'Sentence Equivalence',
        icon: <FiZap />,
        stats:
          sentenceEquivalenceData.totalQuestions > 0
            ? [
                {
                  label: 'Questions Attempted',
                  value: sentenceEquivalenceData.totalQuestions,
                },
                {
                  label: 'Accuracy',
                  value: `${Math.round(sentenceEquivalenceData.accuracy)}%`,
                },
                {
                  label: 'Word Pairs Mastered',
                  value: sentenceEquivalenceData.wordPairsMastered,
                },
                {
                  label: 'Readiness Score',
                  value: `${Math.round(sentenceEquivalenceData.readinessScore)}%`,
                },
              ]
            : [
                { label: 'Practice Available', value: 'Ready' },
                {
                  label: 'Estimated Accuracy',
                  value: `${Math.round(Math.min(sentenceEquivalenceData.accuracy + 5, 85))}%`,
                },
                { label: 'Status', value: 'Not Started' },
                { label: 'Target Score', value: '80%+' },
              ],
        route: '/exams/gre/',
        activeComponent: 'sentence',
      },
      {
        key: 'readingComprehension',
        title: 'Reading Comprehension',
        icon: <FiClock />,
        stats:
          readingComprehensionData.totalQuestions > 0
            ? [
                {
                  label: 'Passages Completed',
                  value: readingComprehensionData.totalPassages,
                },
                {
                  label: 'Accuracy',
                  value: `${Math.round(readingComprehensionData.accuracy)}%`,
                },
                {
                  label: 'Avg Reading Speed',
                  value: `${Math.round(readingComprehensionData.averageReadingSpeed)} wpm`,
                },
                { label: 'Target Score', value: '75%+' },
              ]
            : [
                { label: 'Passages Completed', value: 0 },
                {
                  label: 'Estimated Accuracy',
                  value: `${Math.round(Math.min(readingComprehensionData.accuracy - 5, 75))}%`,
                },
                { label: 'Status', value: 'Not Started' },
                { label: 'Target Score', value: '75%+' },
              ],
        route: '/exams/gre/',
        activeComponent: 'reading',
      },
    ];

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        {components.map((component) => {
          const componentScore =
            readinessData?.componentScores?.[component.key];
          const isReady = componentScore?.ready || false;
          return (
            <div
              key={component.key}
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: `2px solid ${isReady ? 'var(--liwaza-success)' : 'var(--liwaza-outline-variant)'}`,
                position: 'relative',
              }}
            >
              {isReady && (
                <div
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'var(--liwaza-success)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}
                >
                  <FiCheck size={12} />
                  READY
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                <div
                  style={{
                    background: isReady
                      ? 'var(--liwaza-success-container)'
                      : 'var(--liwaza-primary-container)',
                    color: isReady
                      ? 'var(--liwaza-success)'
                      : 'var(--liwaza-primary)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {component.icon}
                </div>
                <h3
                  style={{
                    color: 'var(--liwaza-on-surface)',
                    margin: 0,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  {component.title}
                </h3>
              </div>

              {componentScore && (
                <div
                  style={{
                    background: 'var(--liwaza-surface-container-highest)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.9rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Readiness Score{' '}
                      {componentScore.estimated ? '(Estimated)' : ''}
                    </span>
                    <span
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: componentScore.ready
                          ? 'var(--liwaza-success)'
                          : 'var(--liwaza-warning)',
                      }}
                    >
                      {Math.round(componentScore.score)}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: '6px',
                      background: 'var(--liwaza-surface-container)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${componentScore.score}%`,
                        height: '100%',
                        background: componentScore.ready
                          ? 'var(--liwaza-success)'
                          : 'var(--liwaza-warning)',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                {component.stats.map((stat, index) => (
                  <div
                    key={index}
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: 'var(--liwaza-on-surface)',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  navigate(component.route, {
                    state: { activeCategory: component.activeComponent },
                  });
                }}
                style={{
                  width: '100%',
                  background: isReady
                    ? 'var(--liwaza-success)'
                    : 'var(--liwaza-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {isReady ? 'Review & Practice' : 'Start Practice'}
                <FiArrowRight />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const openPractice = (activeCategory) => {
    navigate('/exams/gre/', { state: { activeCategory } });
  };

  const renderStruggleSpots = () => {
    if (!progressData) return null;

    const spots = getStruggleSpotsFromComprehensive(progressData);
    const hasPractice =
      spots.practiceCounts.textCompletion +
        spots.practiceCounts.sentenceEquivalence +
        spots.practiceCounts.readingQuestions >
      0;
    const hasSpots =
      spots.words.length > 0 ||
      spots.readingSkills.length > 0 ||
      spots.passageTypes.length > 0 ||
      spots.other.length > 0;

    const sourceLabel = {
      text_completion: 'Text Completion',
      sentence_equivalence: 'Sentence Equivalence',
    };
    const sourceCategory = {
      text_completion: 'textcompletion',
      sentence_equivalence: 'sentence',
    };

    const cardStyle = {
      background: 'var(--liwaza-surface-container-highest)',
      borderRadius: '10px',
      padding: '0.9rem 1rem',
    };

    return (
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h3
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '0.35rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiAlertCircle />
          Still struggling
        </h3>
        <p
          style={{
            margin: '0 0 1.25rem',
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '0.95rem',
          }}
        >
          Words you still miss in text completion and sentence equivalence, plus
          the reading question types and passages that keep costing points.
        </p>

        {!hasPractice && (
          <p style={{ margin: 0, color: 'var(--liwaza-on-surface-variant)' }}>
            Practice text completion, sentence equivalence, and reading
            comprehension. Missed words and question types will show up here.
          </p>
        )}

        {hasPractice && !hasSpots && (
          <p style={{ margin: 0, color: 'var(--liwaza-on-surface-variant)' }}>
            Nothing recurring is holding you back in the questions you have
            already practiced.
          </p>
        )}

        {spots.words.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem' }}>Words to rebuild</h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: '0.75rem',
              }}
            >
              {spots.words.map((item) => {
                const sources = (item.sources || [])
                  .map((source) => sourceLabel[source])
                  .filter(Boolean)
                  .join(' · ');
                return (
                <div key={item.word} style={cardStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      alignItems: 'baseline',
                    }}
                  >
                    <strong style={{ fontSize: '1.05rem' }}>{item.word}</strong>
                    <span
                      style={{
                        color:
                          item.accuracy < 50
                            ? 'var(--liwaza-error)'
                            : 'var(--liwaza-warning)',
                        fontWeight: 700,
                      }}
                    >
                      {Math.round(item.accuracy)}%
                    </span>
                  </div>
                  {item.definition && (
                    <p
                      style={{
                      margin: '0.35rem 0 0.5rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.85rem',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                    >
                      {item.definition}
                    </p>
                  )}
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      marginBottom: '0.55rem',
                    }}
                  >
                    Missed {item.misses} of {item.attempts}
                    {sources ? ` · ${sources}` : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openPractice(
                        sourceCategory[item.primary_source] || 'textcompletion'
                      )
                    }
                    style={{
                      background: 'transparent',
                      color: 'var(--liwaza-primary)',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    Practice this
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {spots.readingSkills.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem' }}>
              Reading question types
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {spots.readingSkills.map((skill) => (
                <div key={skill.skill} style={cardStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      marginBottom: '0.45rem',
                    }}
                  >
                    <strong>{skill.label}</strong>
                    <span>
                      {Math.round(skill.accuracy)}% · {skill.correct}/
                      {skill.attempted}
                    </span>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      borderRadius: '999px',
                      background: 'var(--liwaza-surface-container)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(4, Math.min(100, skill.accuracy))}%`,
                        height: '100%',
                        background:
                          skill.accuracy < 50
                            ? 'var(--liwaza-error)'
                            : 'var(--liwaza-warning)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => openPractice('reading')}
              style={{
                marginTop: '0.75rem',
                background: 'var(--liwaza-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.55rem 0.9rem',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Practice reading
            </button>
          </div>
        )}

        {(spots.passageTypes.length > 0 || spots.other.length > 0) && (
          <div>
            <h4 style={{ margin: '0 0 0.75rem' }}>Also costing points</h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '0.75rem',
              }}
            >
              {spots.passageTypes.map((passage) => (
                <div key={passage.topic} style={cardStyle}>
                  <strong>{passage.label}</strong>
                  <div
                    style={{
                      marginTop: '0.35rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.85rem',
                    }}
                  >
                    {Math.round(passage.avg_score)}% average across{' '}
                    {passage.completed} passages
                  </div>
                </div>
              ))}
              {spots.other.map((item) => (
                <div key={item.kind} style={cardStyle}>
                  <strong>{item.label}</strong>
                  <div
                    style={{
                      marginTop: '0.35rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.85rem',
                    }}
                  >
                    {item.detail}. Target: {item.target}.
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderReadinessBreakdown = () => {
    const breakdown = readinessData?.breakdown;
    if (!breakdown || Object.keys(breakdown).length === 0) return null;

    const items = [
      { key: 'coverage', label: 'Coverage' },
      { key: 'accuracy', label: 'Accuracy' },
      { key: 'trend', label: 'Trend' },
      { key: 'pacing', label: 'Pacing' },
      { key: 'consistency', label: 'Consistency' },
    ];

    return (
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h3
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiPercent />
          Readiness Breakdown
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1rem',
          }}
        >
          {items.map((item) => (
            <div
              key={item.key}
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--liwaza-primary)',
                }}
              >
                {Math.round(breakdown[item.key] || 0)}%
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--liwaza-on-surface-variant)',
                }}
              >
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPerformanceInsights = () => {
    const perf = readinessData?.performanceAnalytics;
    if (!perf) return null;

    const strengths = perf.strengths || [];
    const areas = perf.areasForImprovement || [];
    if (strengths.length === 0 && areas.length === 0) return null;

    return (
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h3
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiBarChart />
          Performance Insights
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
          }}
        >
          <div>
            <h4 style={{ margin: '0 0 0.5rem' }}>Strengths</h4>
            {strengths.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {strengths.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, opacity: 0.7 }}>Keep practicing to unlock strengths.</p>
            )}
          </div>
          <div>
            <h4 style={{ margin: '0 0 0.5rem' }}>Areas to Improve</h4>
            {areas.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {areas.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, opacity: 0.7 }}>No major weak spots detected.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRecommendations = () => {
    if (!readinessData?.recommendations?.length) return null;

    return (
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
        }}
      >
        <h3
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiTarget />
          Personalized Recommendations
        </h3>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {readinessData.recommendations.map((rec, index) => (
            <div
              key={index}
              style={{
                background:
                  rec.priority === 'high'
                    ? 'var(--liwaza-error-container)'
                    : 'var(--liwaza-warning-container)',
                color:
                  rec.priority === 'high'
                    ? 'var(--liwaza-on-error-container)'
                    : 'var(--liwaza-on-warning-container)',
                padding: '1rem',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.25rem',
                  }}
                >
                  {rec.component}
                </div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    opacity: 0.9,
                  }}
                >
                  {rec.message}
                </div>
              </div>
              <button
                onClick={() => {
                  navigate(rec.route, {
                    state: { activeCategory: rec.activeComponent },
                  });
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'inherit',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  marginLeft: '1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {rec.action}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOverallStats = () => {
    if (!progressData) return null;

    // Extract component-specific data using our helper functions
    const vocabularyData = getVocabularyProgressFromComprehensive(
      progressData,
      setToast
    );
    const readingComprehensionData =
      getReadingComprehensionProgressFromComprehensive(progressData, setToast);
    const overallSummary = getOverallSummaryFromComprehensive(
      progressData,
      setToast
    );

    const stats = [
      {
        icon: <FiBookOpen />,
        label: 'Words Mastered',
        value: vocabularyData.masteredWords || 0,
        color: 'var(--liwaza-primary)',
        tooltip:
          "Number of vocabulary words you've successfully learned and can recall consistently. Mastering 800+ words significantly improves your chances of scoring 160+ on GRE Verbal.",
      },
      {
        icon: <FiTarget />,
        label: 'Passages Completed',
        value: readingComprehensionData.totalPassages || 0,
        color: 'var(--liwaza-secondary)',
        tooltip:
          "Total reading comprehension passages you've completed. Each passage helps improve your reading speed and comprehension skills, essential for time management in the GRE.",
      },
      {
        icon: <FiCheck />,
        label: 'Overall Accuracy',
        value: `${Math.round(vocabularyData.accuracy || 0)}%`,
        color: 'var(--liwaza-success)',
        tooltip:
          'Your average accuracy across all verbal practice. Higher accuracy indicates better understanding and retention. Aim for 85%+ accuracy to be ready for elite scores.',
      },
      {
        icon: <FiActivity />,
        label: 'Current Streak',
        value: `${vocabularyData.streak || 0} days`,
        color: 'var(--liwaza-warning)',
        tooltip:
          "Number of consecutive days you've practiced. Consistent daily practice is crucial for vocabulary retention and building confidence. Maintain a 7+ day streak for optimal results.",
      },
      {
        icon: <FiClock />,
        label: 'Total Study Time',
        value: `${Math.round((overallSummary.totalStudyTime || 0) / 60)} hrs`,
        color: 'var(--liwaza-tertiary)',
        tooltip:
          'Total time spent on GRE Verbal preparation. Quality practice typically requires 40-80 hours for significant improvement. Track your progress to ensure efficient study habits.',
      },
      {
        icon: <FiStar />,
        label: 'Estimated Verbal Score',
        value: overallSummary.estimatedVerbalScore || 'TBD',
        color: 'var(--liwaza-primary)',
        tooltip:
          'Your projected GRE Verbal score (130-170 scale) based on current performance. This estimate becomes more accurate as you complete more practice. Target 160+ for top programs.',
      },
      {
        icon: <FiClock />,
        label: 'Reading Speed',
        value: `${Math.round(vocabularyData.averageWPM || readingComprehensionData.averageReadingSpeed || 0)} WPM`,
        color: 'var(--liwaza-info)',
        tooltip:
          'Your average reading speed in words per minute across vocabulary articles and reading comprehension passages. A good reading speed for GRE is 250-350 WPM. Higher speed helps with time management.',
      },
    ];

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        {stats.map((stat, index) => (
          <div
            key={index}
            style={{
              background: 'var(--liwaza-surface-container-highest)',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'center',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <div
              style={{
                background: `${stat.color}20`,
                color: stat.color,
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '1.2rem',
              }}
            >
              {stat.icon}
            </div>
            <div
              style={{
                fontSize: '1.8rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-on-surface)',
                marginBottom: '0.25rem',
              }}
            >
              {stat.value}
            </div>
            <div
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
              }}
            >
              {stat.label}
              <InfoTooltip
                content={stat.tooltip}
                position="bottom"
                maxWidth="280px"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                fontSize: '2.5rem',
                color: 'var(--liwaza-on-surface)',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <FiBarChart />
              My Verbal Progress
            </h1>
            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '1.1rem',
                marginBottom: 0,
              }}
            >
              Track your comprehensive GRE Verbal preparation progress and
              readiness for an elite score.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadProgressData(true)}
            style={{
              background: 'var(--liwaza-surface-container)',
              color: 'var(--liwaza-on-surface)',
              border: '1px solid var(--liwaza-outline)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>
      {displayToast(toast)}
      {renderReadinessScore()}
      {renderReadinessBreakdown()}
      {renderStruggleSpots()}
      {renderPerformanceInsights()}
      {renderOverallStats()}

      {/* Weekly Progression Analysis */}
      <div style={{ marginBottom: '2rem' }}>
        <h2
          style={{
            fontSize: '1.8rem',
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiTrendingUp />
          Weekly Progression
        </h2>

        {/* Two Week Verbal Progression */}
        {progressData?.two_week_verbal_progression && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h3
              style={{
                marginBottom: '1rem',
                color: 'var(--liwaza-on-surface)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <FiActivity /> Daily Verbal Progress (Last 14 Days)
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              {progressData.two_week_verbal_progression.daily_progression
                .slice(-14)
                .map((day, index) => {
                  const dayDate = new Date(day.date);
                  const isToday =
                    dayDate.toDateString() === new Date().toDateString();
                  const hasActivity =
                    day.words_practiced > 0 || day.exercises_attempted > 0;

                  return (
                    <div
                      key={day.date}
                      style={{
                        background: hasActivity
                          ? isToday
                            ? 'var(--liwaza-primary-container)'
                            : 'var(--liwaza-surface-container-highest)'
                          : 'var(--liwaza-outline-variant)',
                        padding: '0.75rem 0.5rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: isToday
                          ? '2px solid var(--liwaza-primary)'
                          : '1px solid transparent',
                        opacity: hasActivity ? 1 : 0.6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.7rem',
                          color: 'var(--liwaza-on-surface-variant)',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                        }}
                      >
                        {dayDate.toLocaleDateString([], {
                          weekday: 'short',
                          month: 'numeric',
                          day: 'numeric',
                        })}
                      </div>

                      {hasActivity ? (
                        <>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.25rem',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  color: 'var(--liwaza-primary)',
                                }}
                              >
                                {day.words_practiced}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.6rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                words
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  color: 'var(--liwaza-secondary)',
                                }}
                              >
                                {Math.round(day.accuracy || 0)}%
                              </div>
                              <div
                                style={{
                                  fontSize: '0.6rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                accuracy
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.25rem',
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  color: 'var(--liwaza-success)',
                                }}
                              >
                                {day.words_mastered}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.6rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                mastered
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  color: 'var(--liwaza-tertiary)',
                                }}
                              >
                                {day.avg_wpm || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.6rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                WPM
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--liwaza-on-surface-variant)',
                            opacity: 0.7,
                            marginTop: '1rem',
                          }}
                        >
                          No practice
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Two Week Summary */}
            {progressData.two_week_verbal_progression.summary && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--liwaza-outline-variant)',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-primary)',
                    }}
                  >
                    {
                      progressData.two_week_verbal_progression.summary
                        .active_days
                    }
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    Active Days
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-success)',
                    }}
                  >
                    {
                      progressData.two_week_verbal_progression.summary
                        .total_words_practiced
                    }
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    Words Practiced
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-secondary)',
                    }}
                  >
                    {
                      progressData.two_week_verbal_progression.summary
                        .total_articles_read
                    }
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    Articles Read
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-tertiary)',
                    }}
                  >
                    {Math.round(
                      progressData.two_week_verbal_progression.summary
                        .average_accuracy
                    )}
                    %
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    Avg Accuracy
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-warning)',
                    }}
                  >
                    {
                      progressData.two_week_verbal_progression.summary
                        .total_exercises_attempted
                    }
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    Exercises Done
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vocabulary Weekly Progress */}
        {progressData?.vocabulary_mastery?.weekly_progress &&
          Object.keys(progressData.vocabulary_mastery.weekly_progress).length >
            0 && (
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: '1.5rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                border: '1px solid var(--liwaza-outline-variant)',
              }}
            >
              <h3
                style={{
                  marginBottom: '1rem',
                  color: 'var(--liwaza-on-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiBookOpen /> Vocabulary Learning Progress
              </h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '0.5rem',
                }}
              >
                {Object.entries(progressData.vocabulary_mastery.weekly_progress)
                  .slice(-7) // Show last 7 days
                  .map(([date, wordsLearned]) => {
                    const dayDate = new Date(date);
                    const isToday =
                      dayDate.toDateString() === new Date().toDateString();

                    return (
                      <div
                        key={date}
                        style={{
                          background:
                            wordsLearned > 0
                              ? isToday
                                ? 'var(--liwaza-primary-container)'
                                : 'var(--liwaza-surface-container-highest)'
                              : 'var(--liwaza-outline-variant)',
                          padding: '0.75rem 0.5rem',
                          borderRadius: '6px',
                          textAlign: 'center',
                          border: isToday
                            ? '2px solid var(--liwaza-primary)'
                            : '1px solid transparent',
                          opacity: wordsLearned > 0 ? 1 : 0.6,
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: 'var(--liwaza-on-surface-variant)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {dayDate.toLocaleDateString([], {
                            weekday: 'short',
                            month: 'numeric',
                            day: 'numeric',
                          })}
                        </div>

                        {wordsLearned > 0 ? (
                          <>
                            <div
                              style={{
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                color: 'var(--liwaza-primary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {wordsLearned}
                            </div>
                            <div
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--liwaza-on-surface-variant)',
                              }}
                            >
                              words learned
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: 'var(--liwaza-on-surface-variant)',
                              opacity: 0.7,
                            }}
                          >
                            No words
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
      </div>

      {renderComponentProgress()}
      {renderRecommendations()}
    </div>
  );
}
